import { useState, useEffect } from 'react';
import {
  Text,
  Card,
  makeStyles,
  tokens,
  Spinner,
} from '@fluentui/react-components';
import { useApiClient, customerService, CustomerStats, TenantRecord } from '../../services/apiService';
import { SPACING, TYPOGRAPHY } from '../../styles/constants';

const useStyles = makeStyles({
  page: { maxWidth: '900px', padding: SPACING.lg },
  title: { ...TYPOGRAPHY.h2, marginBottom: SPACING.xs },
  subtitle: { ...TYPOGRAPHY.bodySmall, color: tokens.colorNeutralForeground3, marginBottom: SPACING.xl },
  kpiRow: { display: 'flex', gap: SPACING.lg, marginBottom: SPACING.xl, flexWrap: 'wrap' },
  kpiCard: { flex: 1, minWidth: '200px', padding: SPACING.xl, textAlign: 'center' },
  kpiValue: { fontSize: '36px', fontWeight: 700, color: tokens.colorBrandForeground1 },
  kpiLabel: { ...TYPOGRAPHY.bodySmall, color: tokens.colorNeutralForeground3, marginTop: SPACING.sm },
  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SPACING.lg, '@media (max-width: 640px)': { gridTemplateColumns: '1fr' } },
  sectionCard: { padding: SPACING.xl },
  sectionTitle: { ...TYPOGRAPHY.h3, marginBottom: SPACING.md },
  bar: { display: 'flex', alignItems: 'center', gap: SPACING.md, marginBottom: SPACING.md },
  barLabel: { ...TYPOGRAPHY.body, width: '160px', flexShrink: 0 },
  barTrack: {
    flex: 1,
    height: '8px',
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: '4px',
    overflow: 'hidden',
  },
  barFill: {
    height: '100%',
    backgroundColor: tokens.colorBrandBackground,
    borderRadius: '4px',
  },
  barCount: { ...TYPOGRAPHY.bodySmall, color: tokens.colorNeutralForeground3, width: '24px', textAlign: 'right' },
  empty: { ...TYPOGRAPHY.bodySmall, color: tokens.colorNeutralForeground3, fontStyle: 'italic' },
  trialBanner: {
    backgroundColor: tokens.colorPaletteYellowBackground2,
    border: `1px solid ${tokens.colorPaletteYellowBorderActive}`,
    borderRadius: '6px',
    padding: `${SPACING.md} ${SPACING.lg}`,
    marginBottom: SPACING.xl,
    ...TYPOGRAPHY.body,
  },
});

function formatCategory(cat: string): string {
  const map: Record<string, string> = {
    document_work: 'Documents & reports',
    meetings: 'Meeting notes',
    automation: 'Automation',
    agent_building: 'Building agents',
    tier_comparison: 'Licence comparison',
    process_mapping: 'Process mapping',
    data_integration: 'Data & integrations',
    other: 'Other',
  };
  return map[cat] ?? cat;
}

function formatRole(role: string): string {
  const map: Record<string, string> = {
    finance: 'Finance & Accounting', hr: 'HR & People', sales: 'Sales & Commercial',
    operations: 'Operations', it: 'IT & Technology', exec: 'Leadership',
    legal: 'Legal & Compliance', marketing: 'Marketing & Comms',
  };
  return map[role] ?? role;
}

function BarRow({ label, count, max }: { label: string; count: number; max: number }) {
  const styles = useStyles();
  const pct = max > 0 ? (count / max) * 100 : 0;
  return (
    <div className={styles.bar}>
      <Text className={styles.barLabel} title={label}>{label.length > 22 ? label.slice(0, 22) + '…' : label}</Text>
      <div className={styles.barTrack}>
        <div className={styles.barFill} style={{ width: `${pct}%` }} />
      </div>
      <Text className={styles.barCount}>{count}</Text>
    </div>
  );
}

export default function CustomerDashboard() {
  const styles = useStyles();
  useApiClient();

  const [tenant, setTenant] = useState<TenantRecord | null>(null);
  const [stats, setStats] = useState<CustomerStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([customerService.getMe(), customerService.getStats()])
      .then(([t, s]) => { setTenant(t); setStats(s); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ padding: SPACING.xxl, textAlign: 'center' }}><Spinner /></div>;

  const maxCat = Math.max(...(stats?.topCategories?.map((c) => c.count) ?? [1]));
  const maxRole = Math.max(...(stats?.roleBreakdown?.map((r) => r.count) ?? [1]));
  const maxGoal = Math.max(...(stats?.goalBreakdown?.map((g) => g.count) ?? [1]));

  return (
    <div className={styles.page}>
      <Text className={styles.title}>Usage Dashboard</Text>
      <Text className={styles.subtitle}>Last 30 days · {tenant?.tenant_name}</Text>

      {tenant?.subscription_tier === 'trial' && (
        <div className={styles.trialBanner}>
          You are on a free trial. Contact your Sherpai account manager to discuss a subscription.
        </div>
      )}

      <div className={styles.kpiRow}>
        <Card className={styles.kpiCard}>
          <Text className={styles.kpiValue}>{stats?.activeUsers ?? 0}</Text>
          <Text className={styles.kpiLabel}>Active users</Text>
        </Card>
        <Card className={styles.kpiCard}>
          <Text className={styles.kpiValue}>{stats?.totalConversations ?? 0}</Text>
          <Text className={styles.kpiLabel}>Total conversations</Text>
        </Card>
        <Card className={styles.kpiCard}>
          <Text className={styles.kpiValue}>{stats?.goalBreakdown?.length ?? 0}</Text>
          <Text className={styles.kpiLabel}>AI goals captured</Text>
        </Card>
      </div>

      <div className={styles.grid}>
        <Card className={styles.sectionCard}>
          <Text className={styles.sectionTitle}>Top topics</Text>
          {stats?.topCategories?.length ? (
            stats.topCategories.map((c) => (
              <BarRow key={c.category} label={formatCategory(c.category)} count={c.count} max={maxCat} />
            ))
          ) : (
            <Text className={styles.empty}>No conversations yet</Text>
          )}
        </Card>

        <Card className={styles.sectionCard}>
          <Text className={styles.sectionTitle}>Users by role</Text>
          {stats?.roleBreakdown?.length ? (
            stats.roleBreakdown.map((r) => (
              <BarRow key={r.role} label={formatRole(r.role)} count={r.count} max={maxRole} />
            ))
          ) : (
            <Text className={styles.empty}>No role data yet</Text>
          )}
        </Card>

        <Card className={styles.sectionCard} style={{ gridColumn: '1 / -1' }}>
          <Text className={styles.sectionTitle}>What your team wants from AI</Text>
          {stats?.goalBreakdown?.length ? (
            stats.goalBreakdown.map((g) => (
              <BarRow key={g.goal} label={g.goal} count={g.count} max={maxGoal} />
            ))
          ) : (
            <Text className={styles.empty}>No goals captured yet — users are prompted after their first verification</Text>
          )}
        </Card>
      </div>
    </div>
  );
}
